import React, {useContext, useState, createContext, useCallback} from 'react'
import _ from 'lodash'
import {isClient} from 'src/common/razzle-platform-components/utils'
import {useSelector} from 'react-redux'
import { checkRight } from 'src/common/razzle-platform-user/helpers'

export type IAuthenticationContext = {
    isAuthenticationDialogOpen: boolean
    actions: {
        display: string
        executeAfterLogin: () => void | null
        authorization?: {
            requiredRight?: boolean | null
            description?: null | string
            buttonTitle?: null | string,
            redirectTo?: null | string
        }
    },
    displayLogin?: () => void
    displayRegister?: () => void
    displayAuthorization?: () => void
    closeAuthenticationDialog?: () => void
    isAuthenticated?: () => boolean
    onAuth?: (usr: any) => void
}

export const AuthenticationContext = createContext<IAuthenticationContext>({
    isAuthenticationDialogOpen: false,
    actions: {
        display: 'login',
        executeAfterLogin: null
    }
})


export const useAuth = () => useContext(AuthenticationContext)

type ActionState = {
    display?: string
    executeAfterLogin?: () => void | null
    authorization?: {
        requiredRight?: boolean | null
        description?: null | string
        buttonTitle?: null | string,
        redirectTo?: null | string
    }
}


export const useAuthDialog = () => {
    const [isAuthenticationDialogOpen, setAuthenticationDialogOpen] = useState<boolean>(false)
    const [actions, setActions] = useState<ActionState>({
        display: 'login',
        executeAfterLogin: null,
        authorization: {
            requiredRight: null,
            description: null,
            buttonTitle: null,
            redirectTo: null
        }
    })
    // @ts-ignore
    const user = useSelector(({userModel}) => userModel.user)

    const closeAuthenticationDialog = () => {
        setAuthenticationDialogOpen(false)
        setActions({executeAfterLogin: null})
    }


    const isAuthenticated = () => {
        if (isClient) {
            return Boolean(localStorage.getItem('user') && localStorage.getItem('authentication_token'))
        }
        // on the server part we always return false
        return false
    }

    const onAuth = (executeAfterLogin, authorization) => {
        if (executeAfterLogin) {
            if (authorization) {
                if (isAuthenticated()) {
                    if (!authorization.requiredRight) throw new Error('"requiredRight" value is not valid')

                    const hasAccessRight = checkRight(user.rights, authorization.requiredRight)
                    if (hasAccessRight) {
                        executeAfterLogin(user)
                    } else {
                        setActions({
                            ...actions,
                            display: 'authorization',
                            authorization: {
                                ...actions.authorization,
                                ...authorization,
                            }
                        })
                        setAuthenticationDialogOpen(true)
                    }

                } else {
                    setAuthenticationDialogOpen(true)
                    setActions({
                        ...actions,
                        display: 'login',
                        executeAfterLogin,
                        authorization: {
                            ...actions.authorization,
                            ...authorization,
                        }
                    })
                }
            } else {
                if (isAuthenticated()) {
                    executeAfterLogin(user)
                } else {
                    setAuthenticationDialogOpen(true)
                    setActions({...actions, display: 'login', executeAfterLogin, authorization: null})
                }
            }
        }
    }


    const displayLogin = () => {
        setActions({...actions, display: 'login'})
    }

    const displayRegister = () => {
        setActions({...actions, display: 'register'})
    }

    const displayAuthorization = () => {
        setActions({...actions, display: 'authorization'})
    }

    return { isAuthenticationDialogOpen, displayLogin, displayRegister, displayAuthorization, onAuth, closeAuthenticationDialog, actions, isAuthenticated }
}
